import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@vibe/core";
import PageLoader from "../PageLoader";
import useUser from "../../hooks/context/userHook";
import useTokenHook from "../../hooks/context/useTokenHook";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import useUnlinkStatusStream, {
  UnlinkCompletedEvent,
} from "../../hooks/services/useUnlinkStatusStream";
import { getUnlinkItemsStatus } from "../../utils/requests/itemRequests";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { loadUnlinkCandidates, startBatchUnlink, UnlinkCandidate } from "./batchUnlink";

type Phase = "loading" | "load_error" | "review" | "unlinking" | "done";

/** Where one item of an orchestrated unlink stands. */
interface ItemOutcome {
  status: "pending" | "done" | "failed" | "not_synced";
  error?: string | null;
}

/**
 * How often to ask the backend where the click stands while outcomes are still due. The
 * socket normally delivers them; this covers one lost while it was down.
 */
const RECONCILE_INTERVAL_MS = 8000;

const outcomeDetail = (outcome: ItemOutcome | undefined): string => {
  if (!outcome || outcome.status === "pending") return "Unlinking…";
  if (outcome.status === "done") return "Unlinked";
  if (outcome.status === "not_synced") return "Not synced";
  return outcome.error ? `Failed: ${outcome.error}` : "Failed";
};

const pluralItems = (count: number) => `${count} item${count === 1 ? "" : "s"}`;

const closeView = () => {
  MondayApiQueryInstance.closeAppFeatureModal().catch(() => {});
};

const candidateDetail = (candidate: UnlinkCandidate): string => {
  if (candidate.status === "not_synced") return "Not synced";
  if (candidate.status === "check_failed") return "Couldn't check";
  return candidate.connectedBoards.length
    ? candidate.connectedBoards.join(", ")
    : "No connected boards";
};

const BatchUnlinkPanel = () => {
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();
  const { itemIds, currentBoard } = useBoardAndItems();

  const [phase, setPhase] = useState<Phase>("loading");
  const [candidates, setCandidates] = useState<UnlinkCandidate[]>([]);
  const [progress, setProgress] = useState(0);
  const [queuedCount, setQueuedCount] = useState(0);
  const [failedIds, setFailedIds] = useState<string[]>([]);
  // Legacy path: outcomes reported back by simb-pulse-ms, which carry no item id.
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [backgroundErrors, setBackgroundErrors] = useState<string[]>([]);
  // Orchestrated path: one outcome per item, keyed on the requests this view started.
  const [outcomes, setOutcomes] = useState<Record<string, ItemOutcome>>({});
  const requestIdsRef = useRef<Set<string>>(new Set());

  const settle = useCallback((itemId: string, outcome: ItemOutcome) => {
    setOutcomes((previous) =>
      // Only an item this view is waiting on, and never back out of a terminal outcome.
      previous[itemId]?.status === "pending" ? { ...previous, [itemId]: outcome } : previous,
    );
  }, []);

  const reconcile = useCallback(async () => {
    if (!token) return;
    for (const requestId of requestIdsRef.current) {
      try {
        for (const item of await getUnlinkItemsStatus(token, requestId)) {
          if (item.status === "done") settle(item.itemId, { status: "done" });
          if (item.status === "dead") settle(item.itemId, { status: "failed", error: item.error });
        }
      } catch {
        // Best effort: the next tick, or the socket, will catch up.
      }
    }
  }, [token, settle]);

  useUnlinkStatusStream({
    userId,
    accountId,
    onItemResult: (event: UnlinkCompletedEvent) => {
      if (!requestIdsRef.current.has(event.requestId)) return;
      settle(
        event.itemId,
        event.status === "dead"
          ? { status: "failed", error: event.error }
          : { status: event.status },
      );
    },
    onReconnect: reconcile,
    onUnlinked: () => setConfirmedCount((previous) => previous + 1),
    onFailed: (message) => setBackgroundErrors((previous) => [...previous, message]),
  });

  const hasPendingOutcome = Object.values(outcomes).some((outcome) => outcome.status === "pending");

  useEffect(() => {
    if (!hasPendingOutcome) return;
    const timer = setInterval(reconcile, RECONCILE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasPendingOutcome, reconcile]);

  const itemIdsKey = (itemIds ?? []).join(",");

  const load = useCallback(async () => {
    if (!token || !itemIdsKey) return;
    setPhase("loading");
    try {
      setCandidates(await loadUnlinkCandidates(token, itemIdsKey.split(","), currentBoard, userId));
      setPhase("review");
    } catch {
      setPhase("load_error");
    }
  }, [token, itemIdsKey, currentBoard, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const synced = useMemo(
    () => candidates.filter((candidate) => candidate.status === "synced"),
    [candidates],
  );

  const runUnlink = async (targets: UnlinkCandidate[]) => {
    if (!token || targets.length === 0) return;
    setProgress(0);
    setPhase("unlinking");
    const result = await startBatchUnlink(token, targets, setProgress);

    if (result.mode === "orchestrated" && result.requestId) {
      requestIdsRef.current.add(result.requestId);
      setOutcomes((previous) => ({
        ...previous,
        ...Object.fromEntries(
          result.queued.map((itemId) => [itemId, { status: "pending" } as ItemOutcome]),
        ),
      }));
    } else {
      setQueuedCount((previous) => previous + result.queued.length);
    }

    setFailedIds(result.failed);
    setPhase("done");
  };

  // The legacy stream is per user, not per request, so a stray status can never overcount.
  const confirmed = Math.min(confirmedCount, queuedCount);

  // Orchestrated rows, in the order the user reviewed them.
  const tracked = synced.filter((candidate) => outcomes[candidate.itemId]);
  const unlinkedCount = tracked.filter(
    (candidate) => outcomes[candidate.itemId].status === "done",
  ).length;
  const deadIds = tracked
    .filter((candidate) => outcomes[candidate.itemId].status === "failed")
    .map((candidate) => candidate.itemId);
  const retryIds = [...new Set([...failedIds, ...deadIds])];

  const retryFailed = () =>
    runUnlink(synced.filter((candidate) => retryIds.includes(candidate.itemId)));

  if (phase === "loading") {
    return <PageLoader />;
  }

  // index.css zeroes every heading and paragraph margin with !important, so the space above
  // a paragraph here is padding (pt-4), never a margin utility.
  return (
    <div className="flex flex-col h-screen px-8 pt-6 pb-4">
      <div>
        <h1 className="font-semibold">Unlink items</h1>
        <p className="text-sm text-[#979797]">Same Item Multiple Boards</p>
      </div>

      {phase === "load_error" && (
        <>
          <p className="pt-4">We couldn't check the selected items. Please try again.</p>
          <div className="flex gap-2 justify-end mt-auto pt-4">
            <Button kind="tertiary" onClick={closeView}>
              Cancel
            </Button>
            <Button onClick={load}>Try again</Button>
          </div>
        </>
      )}

      {(phase === "review" || phase === "unlinking") && (
        <>
          <p className="pt-4">
            {synced.length === 0
              ? "None of the selected items are synced, so there is nothing to unlink."
              : `${synced.length} of ${pluralItems(candidates.length)} selected ${
                  synced.length === 1 ? "is" : "are"
                } synced. Unlinking stops each one from syncing with its connected boards. No items are deleted, and the copies stay on every board.`}
          </p>

          <ul className="flex-1 min-h-0 p-0 mt-4 mb-0 overflow-y-auto list-none border border-solid border-[#d0d4e4] rounded">
            {candidates.map((candidate) => (
              <li
                key={candidate.itemId}
                className="flex gap-4 justify-between px-3 py-2 border-0 border-b border-solid border-[#e6e9ef] last:border-b-0"
              >
                <span className="font-medium truncate">{candidate.name}</span>
                <span
                  className={`text-sm text-right shrink-0 max-w-[55%] truncate ${
                    candidate.status === "synced" ? "" : "text-[#979797]"
                  }`}
                  title={candidateDetail(candidate)}
                >
                  {candidateDetail(candidate)}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex gap-2 justify-end items-center pt-4">
            {phase === "unlinking" && (
              <span className="mr-auto text-sm text-[#979797]">
                Unlinking {progress} of {synced.length}…
              </span>
            )}
            <Button kind="tertiary" onClick={closeView} disabled={phase === "unlinking"}>
              Cancel
            </Button>
            <Button
              color="negative"
              onClick={() => runUnlink(synced)}
              disabled={synced.length === 0}
              loading={phase === "unlinking"}
            >
              {synced.length === 0 ? "Unlink" : `Unlink ${pluralItems(synced.length)}`}
            </Button>
          </div>
        </>
      )}

      {phase === "done" && (
        <>
          {tracked.length > 0 && (
            <>
              <p className="pt-4">
                {hasPendingOutcome
                  ? `Unlinking ${pluralItems(tracked.length)}. ${unlinkedCount} of ${tracked.length} done so far.`
                  : `${unlinkedCount} of ${pluralItems(tracked.length)} unlinked.`}
                {!hasPendingOutcome && deadIds.length > 0 && ` ${deadIds.length} failed.`}
              </p>
              <ul className="flex-1 min-h-0 p-0 mt-4 mb-0 overflow-y-auto list-none border border-solid border-[#d0d4e4] rounded">
                {tracked.map((candidate) => {
                  const outcome = outcomes[candidate.itemId];
                  return (
                    <li
                      key={candidate.itemId}
                      className="flex gap-4 justify-between px-3 py-2 border-0 border-b border-solid border-[#e6e9ef] last:border-b-0"
                    >
                      <span className="font-medium truncate">{candidate.name}</span>
                      <span
                        className={`text-sm text-right shrink-0 max-w-[55%] truncate ${
                          outcome.status === "failed"
                            ? "text-[#d83a52]"
                            : outcome.status === "done"
                              ? ""
                              : "text-[#979797]"
                        }`}
                        title={outcomeDetail(outcome)}
                      >
                        {outcomeDetail(outcome)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {queuedCount > 0 && (
            <p className="pt-4">
              {confirmed >= queuedCount
                ? `${pluralItems(queuedCount)} unlinked.`
                : `${pluralItems(queuedCount)} ${
                    queuedCount === 1 ? "is" : "are"
                  } being unlinked. This finishes in the background, so the boards may take a moment to update.`}
              {confirmed > 0 &&
                confirmed < queuedCount &&
                ` ${confirmed} of ${queuedCount} done so far.`}
            </p>
          )}
          {backgroundErrors.length > 0 && (
            <div className="mt-4 text-[#d83a52]">
              <p>{pluralItems(backgroundErrors.length)} failed to unlink in the background:</p>
              <ul className="pl-5 mt-1 mb-0">
                {backgroundErrors.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
            </div>
          )}
          {failedIds.length > 0 && (
            <p className="pt-4 text-[#d83a52]">
              {pluralItems(failedIds.length)} couldn't be unlinked:{" "}
              {synced
                .filter((candidate) => failedIds.includes(candidate.itemId))
                .map((candidate) => candidate.name)
                .join(", ")}
              .
            </p>
          )}
          {/* No Close after a clean run: Monday's own modal control dismisses the view. The
              footer is only for a run that left something to act on. */}
          {retryIds.length > 0 && (
            <div className="flex gap-2 justify-end mt-auto pt-4">
              {!hasPendingOutcome && (
                <Button kind="secondary" onClick={retryFailed}>
                  Retry failed
                </Button>
              )}
              <Button onClick={closeView}>Close</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BatchUnlinkPanel;
